import type { InternalOpsChannel } from "@/lib/api/internal-ops-messaging";
import {
  classifySupportChannel,
  type SupportChannelBucket,
} from "@/lib/utils/support-channels";
import {
  searchNoticeConversations,
  type NoticeConversation,
} from "./notice-conversations";

export type NoticeSupportGroupKey =
  | "supervisors"
  | "cohorts"
  | "groups"
  | "other";

export interface NoticeSupportGroups {
  supervisors: NoticeConversation[];
  cohorts: NoticeConversation[];
  groups: NoticeConversation[];
  other: NoticeConversation[];
}

export interface NoticeSupportGroupingContext {
  /** Keyed by `sdk_url`, which equals `NoticeConversation.id`. */
  internalByUrl: Map<string, InternalOpsChannel>;
  currentUserId: string;
  isCurrentUserMentor: boolean;
  activeSupervisorId: string | null;
}

function toNoticeSupportGroupKey(
  bucket: SupportChannelBucket,
): NoticeSupportGroupKey {
  switch (bucket) {
    case "supervisors":
      return "supervisors";
    case "cohorts":
      return "cohorts";
    case "groups":
      return "groups";
    case "mentors":
    case "leadMentors":
    case "admins":
    case "other":
      return "other";
    default: {
      const exhaustiveCheck: never = bucket;
      return exhaustiveCheck;
    }
  }
}

export function classifySupportConversation(
  conversation: NoticeConversation,
  context: NoticeSupportGroupingContext,
): NoticeSupportGroupKey {
  return toNoticeSupportGroupKey(
    classifySupportChannel(context.internalByUrl.get(conversation.id), context),
  );
}

export function groupSupportConversations(
  conversations: NoticeConversation[],
  context: NoticeSupportGroupingContext,
): NoticeSupportGroups {
  const groups: NoticeSupportGroups = {
    supervisors: [],
    cohorts: [],
    groups: [],
    other: [],
  };

  for (const conversation of conversations) {
    groups[classifySupportConversation(conversation, context)].push(
      conversation,
    );
  }

  return groups;
}

export function searchNoticeSupportGroups(
  groups: NoticeSupportGroups,
  query: string,
): NoticeSupportGroups {
  return {
    supervisors: searchNoticeConversations(groups.supervisors, query),
    cohorts: searchNoticeConversations(groups.cohorts, query),
    groups: searchNoticeConversations(groups.groups, query),
    other: searchNoticeConversations(groups.other, query),
  };
}

export function isNoticeSupportGroupsEmpty(groups: NoticeSupportGroups): boolean {
  return (
    groups.supervisors.length === 0 &&
    groups.cohorts.length === 0 &&
    groups.groups.length === 0 &&
    groups.other.length === 0
  );
}
